
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { useState } from 'react';
import {
  InlineEdit,
  Text,
  Input,
  Shade,
  Select,
  Label,
  FormGroup,
  Margin,
  Size,
  MultiSelect,
  TextArea,
} from '@drawbotics/react-drylus';
import Playground, { PropsTable } from '~/components/Playground';
export const ControlledInlineEdit = ({
  defaultValue,
  render
}) => {
  const [value, setValue] = useState(defaultValue);
  const [tempValue, onChange] = useState(value);
  return render({
    value,
    setValue,
    tempValue,
    onChange
  });
};
export const options = [{
  value: 'belgium',
  label: 'Belgium'
}, {
  value: 'italy',
  label: 'Italy'
}];

const layoutProps = {
  ControlledInlineEdit,
options
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Inline Edit`}</h1>
<p>{`Used to edit information inline. Useful when the information displayed can be edited and a form appearance is not possible.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { InlineEdit } from '@drawbotics/react-drylus';
`}</code></pre>
<p>{`Note that this is a completely uncontrolled component. You are responsible for managing the state of edits and confirmation. This component simply provides a wrapper to combine a "view" and "edit" state for UI data.`}</p>

<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={0} __code={`<ControlledInlineEdit render={({ value, setValue, tempValue, onChange }) => (
  <InlineEdit
    onCancel={() => onChange(value)}
    onClickConfirm={() => setValue(tempValue)}
    edit={<Input size={Size.SMALL} placeholder="Start typing..." value={tempValue ?? ''} onChange={onChange} />}>
    <Text shade={! value ? Shade.MEDIUM : undefined}>{! value ? 'Click to edit...' : value}</Text>
  </InlineEdit>
)} />`} mode="jsx" mdxType="Playground">
  <ControlledInlineEdit render={({
        value,
        setValue,
        tempValue,
        onChange
      }) => <InlineEdit onCancel={() => onChange(value)} onClickConfirm={() => setValue(tempValue)} edit={<Input size={Size.SMALL} placeholder="Start typing..." value={tempValue ?? ''} onChange={onChange} mdxType="Input" />} mdxType="InlineEdit">
      <Text shade={!value ? Shade.MEDIUM : undefined} mdxType="Text">{!value ? 'Click to edit...' : value}</Text>
    </InlineEdit>} mdxType="ControlledInlineEdit" />
</Playground>
<br />
<PropsTable component={InlineEdit} mdxType="PropsTable" />

<p>{`Here are more examples with different form components and way to display the values:`}</p>
<Playground __position={3} __code={`<>
<Margin size={{ bottom: Size.DEFAULT }}>
  <FormGroup
    label={<Label>With a select</Label>}
    input={
      <ControlledInlineEdit render={({ value, setValue, tempValue, onChange }) => (
        <InlineEdit
          onCancel={() => onChange(value)}
          onClickConfirm={() => setValue(tempValue)}
          edit={
            <Select
              placeholder="Pick option..."
              value={tempValue ?? ''}
              onChange={onChange}
              options={options}/>
            }>
          <Text shade={! value ? Shade.MEDIUM : undefined}>
            {! value ? 'Click to pick option...' : options.find((option) => option.value === value).label}
          </Text>
        </InlineEdit>
      )} />
    } />
</Margin>
<Margin size={{ bottom: Size.DEFAULT }}>
  <FormGroup
    label={<Label>With a multi select</Label>}
    input={
      <ControlledInlineEdit render={({ value, setValue, tempValue, onChange }) => (
        <InlineEdit
          onCancel={() => onChange(value)}
          onClickConfirm={() => setValue(tempValue)}
          edit={
            <MultiSelect
              placeholder="Pick option..."
              values={tempValue ?? []}
              onChange={onChange}
              options={options}/>
            }>
          <Text shade={! value ? Shade.MEDIUM : undefined}>
            {! value
              ? 'Click to pick options...'
              : value?.map((v) => options.find((o) => o.value === v).label).join(', ')
            }
          </Text>
        </InlineEdit>
      )} />
    } />
</Margin>
<FormGroup
  label={<Label>With a textarea</Label>}
  input={
    <ControlledInlineEdit render={({ value, setValue, tempValue, onChange }) => (
      <InlineEdit
        onCancel={() => onChange(value)}
        onClickConfirm={() => setValue(tempValue)}
        edit={<TextArea placeholder="Start typing..." value={tempValue ?? ''} onChange={onChange} />}>
        <Text
          style={{ whiteSpace: 'pre-wrap' }}
          shade={! value ? Shade.MEDIUM : undefined}>
            {! value ? 'Click to edit...' : value}
        </Text>
      </InlineEdit>
    )} />
  } />
</>`} mode="jsx" mdxType="Playground">
  <>
  <Margin size={{
          bottom: Size.DEFAULT
        }} mdxType="Margin">
    <FormGroup label={<Label mdxType="Label">With a select</Label>} input={<ControlledInlineEdit render={({
            value,
            setValue,
            tempValue,
            onChange
          }) => <InlineEdit onCancel={() => onChange(value)} onClickConfirm={() => setValue(tempValue)} edit={<Select placeholder="Pick option..." value={tempValue ?? ''} onChange={onChange} options={options} mdxType="Select" />} mdxType="InlineEdit">
            <Text shade={!value ? Shade.MEDIUM : undefined} mdxType="Text">
              {!value ? 'Click to pick option...' : options.find(option => option.value === value).label}
            </Text>
          </InlineEdit>} mdxType="ControlledInlineEdit" />} mdxType="FormGroup" />
  </Margin>
  <Margin size={{
          bottom: Size.DEFAULT
        }} mdxType="Margin">
    <FormGroup label={<Label mdxType="Label">With a multi select</Label>} input={<ControlledInlineEdit render={({
            value,
            setValue,
            tempValue,
            onChange
          }) => <InlineEdit onCancel={() => onChange(value)} onClickConfirm={() => setValue(tempValue)} edit={<MultiSelect placeholder="Pick option..." values={tempValue ?? []} onChange={onChange} options={options} mdxType="MultiSelect" />} mdxType="InlineEdit">
            <Text shade={!value ? Shade.MEDIUM : undefined} mdxType="Text">
              {!value ? 'Click to pick options...' : value?.map(v => options.find(o => o.value === v).label).join(', ')}
            </Text>
          </InlineEdit>} mdxType="ControlledInlineEdit" />} mdxType="FormGroup" />
  </Margin>
  <FormGroup label={<Label mdxType="Label">With a textarea</Label>} input={<ControlledInlineEdit render={({
          value,
          setValue,
          tempValue,
          onChange
        }) => <InlineEdit onCancel={() => onChange(value)} onClickConfirm={() => setValue(tempValue)} edit={<TextArea placeholder="Start typing..." value={tempValue ?? ''} onChange={onChange} mdxType="TextArea" />} mdxType="InlineEdit">
          <Text style={{
            whiteSpace: 'pre-wrap'
          }} shade={!value ? Shade.MEDIUM : undefined} mdxType="Text">
              {!value ? 'Click to edit...' : value}
          </Text>
        </InlineEdit>} mdxType="ControlledInlineEdit" />} mdxType="FormGroup" />
  </>
</Playground>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;